/**
 * useWriterPresence — tracks writer-tab visibility and emits a
 * "back-after-Xm" signal when the writer returns from an absence
 * of at least ``awayThresholdMs``.
 *
 * Spec 23 P9. Feeds the ``DigestReturnPill`` so writers coming back
 * from lunch / a meeting see a summary of what changed + what wants
 * their attention.
 *
 * Mechanism: ``document.visibilitychange`` — the standard browser /
 * Office-WebView signal for tab/pane visibility. No polling. On
 * hide → record the timestamp. On show → if the gap crosses the
 * threshold, set ``isBack=true`` with the computed ``awayMs``.
 *
 * SRP: the hook observes visibility and nothing else. No dispatch,
 * no side effects beyond the presence state. The shell wires the
 * signal to a component + acknowledgment handler.
 *
 * # DECISION: 5-minute default threshold.
 * # WHY: Short enough that lunch / a meeting trips it, long enough
 * #      that quick context-switches (checking another tab,
 * #      answering a Slack ping) don't. Matches the Spec 22 P10 +
 * #      Spec 23 §5 recommendation. Writers can dismiss the pill if
 * #      it fires too eagerly; the threshold is the prior.
 * # DATE: 2026-04-22
 *
 * # DECISION: ``isBack`` clears on ``acknowledge()`` — not on next
 * #   visibilitychange.
 * # WHY: the writer's dismiss or click-through is the right signal
 * #      for "I saw it"; auto-clearing on re-hide would cause the
 * #      pill to briefly appear + disappear during rapid visibility
 * #      flicker, which is confusing. Explicit acknowledgment is
 * #      honest UX.
 * # DATE: 2026-04-22
 *
 * # DECISION: fail-soft when ``document`` / ``visibilitychange`` are
 * #   unavailable (SSR, some Office.js WebView edge cases).
 * # WHY: the pill is advisory UX; its absence on unusual platforms
 * #      must not crash or degrade the core experience. Hook returns
 * #      ``{awayMs: null, isBack: false, acknowledge: noop}`` in that
 * #      environment.
 * # DATE: 2026-04-22
 */

import { useCallback, useEffect, useRef, useState } from "react";

export const DEFAULT_AWAY_THRESHOLD_MS = 5 * 60 * 1000;

export interface WriterPresence {
  /** Milliseconds the writer was away during the most recent
   *  hidden→visible transition that crossed the threshold. Null
   *  before the first qualifying transition OR after acknowledge. */
  awayMs: number | null;
  /** True iff the last qualifying transition has not yet been
   *  acknowledged. Once the writer clicks (or dismisses) the pill,
   *  caller invokes ``acknowledge()`` to flip this back to false. */
  isBack: boolean;
  /** Mark the return as handled — clears ``isBack`` (and, for
   *  cleanliness, ``awayMs``). Safe to call when nothing pending. */
  acknowledge: () => void;
}

export interface UseWriterPresenceOptions {
  /** Minimum away duration that qualifies as a "return" worth
   *  surfacing. Defaults to 5 minutes. */
  awayThresholdMs?: number;
  /** Gate — hook observes nothing when false. Shell typically
   *  passes ``state.state === "mid-run" || "approval" || "post-run"``
   *  so idle / plan-review never trip the signal. */
  enabled?: boolean;
}

export function useWriterPresence(options: UseWriterPresenceOptions = {}): WriterPresence {
  const { awayThresholdMs = DEFAULT_AWAY_THRESHOLD_MS, enabled = true } = options;

  const [awayMs, setAwayMs] = useState<number | null>(null);
  const [isBack, setIsBack] = useState(false);
  const hiddenSinceRef = useRef<number | null>(null);

  useEffect(() => {
    if (!enabled) {
      hiddenSinceRef.current = null;
      return;
    }
    if (typeof document === "undefined" || !("visibilityState" in document)) {
      return;
    }
    const handler = (): void => {
      if (document.visibilityState === "hidden") {
        hiddenSinceRef.current = Date.now();
        return;
      }
      // visible again — compute the gap, decide whether it crossed
      // the threshold, and update state accordingly.
      const since = hiddenSinceRef.current;
      hiddenSinceRef.current = null;
      if (since === null) return;
      const away = Date.now() - since;
      if (away >= awayThresholdMs) {
        setAwayMs(away);
        setIsBack(true);
      }
    };
    document.addEventListener("visibilitychange", handler);
    return () => {
      document.removeEventListener("visibilitychange", handler);
    };
  }, [enabled, awayThresholdMs]);

  const acknowledge = useCallback(() => {
    setIsBack(false);
    setAwayMs(null);
  }, []);

  return { awayMs, isBack, acknowledge };
}

// ---------------------------------------------------------------------------
// Pure formatter — exported for tests + DigestReturnPill
// ---------------------------------------------------------------------------

/**
 * Format an "away duration" as the compact label the toast uses:
 * '44M' / '2H 15M' / '3H'. Pure; testable. Floors to whole minutes
 * so the label doesn't jitter when values cross second boundaries.
 * Caps at 99H 59M — if someone's been away a week, something else
 * is broken.
 */
export function formatAwayLabel(ms: number): string {
  const mins = Math.max(0, Math.floor(ms / 60_000));
  if (mins < 60) return `${mins}M`;
  const hrs = Math.floor(mins / 60);
  const rem = mins % 60;
  const h = Math.min(hrs, 99);
  return rem > 0 ? `${h}H ${rem}M` : `${h}H`;
}
